import React, { useState, useRef, useEffect, useCallback } from 'react';
// import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { MenuItem, Select, FormControl, InputLabel, Box } from '@mui/material';

const StatCard = ({ label, value, color }) => (
  <div className="stat-card" style={{ background: color }}>
    <div className="stat-value">{value}</div>
    <div className="stat-label">{label}</div>
  </div>
);

const cardSx = {
  minHeight: 340,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 3,
  boxShadow: 2,
  transition: 'box-shadow 0.2s, transform 0.2s',
  '&:hover': {
    boxShadow: 8,
    transform: 'translateY(-4px) scale(1.01)',
  },
};
const cardHeaderSx = {
  pb: 0,
  fontWeight: 700,
  fontSize: '1.15rem',
  color: '#222',
  background: '#fafbfc',
  borderBottom: '1px solid #f3f3f3',
  borderTopLeftRadius: '24px',
  borderTopRightRadius: '24px',
  minHeight: 56,
};
const cardContentSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  pt: 2,
  px: 3,
  pb: 3,
};

// Responsive chart container
const ChartContainer = ({ children, height = 320, minHeight = 240 }) => {
  const ref = useRef();
  const [width, setWidth] = useState(400);

  const handleResize = useCallback(() => {
    if (ref.current) {
      setWidth(ref.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return (
    <div ref={ref} style={{ width: '100%', height, minHeight }}>
      {children(width)}
    </div>
  );
};

const ChartCard = ({ title, children }) => (
  <Card elevation={2} sx={cardSx}>
    <CardHeader title={title} sx={cardHeaderSx} />
    <CardContent sx={cardContentSx}>{children}</CardContent>
  </Card>
);

const Dashboard = ({ org, project, DUMMY_DATA, email }) => {
  const [tab, setTab] = useState('Overview');
  const [pickerType, setPickerType] = useState('month'); // 'date', 'month', 'range'
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [rangeStart, setRangeStart] = useState(dayjs().startOf('month'));
  const [rangeEnd, setRangeEnd] = useState(dayjs().endOf('month'));

  // State for Users - Day Wise chart filter
  const [daysPickerType, setDaysPickerType] = useState('month');
  const [daysSelectedDate, setDaysSelectedDate] = useState(dayjs());
  const [daysSelectedMonth, setDaysSelectedMonth] = useState(dayjs());
  const [daysRangeStart, setDaysRangeStart] = useState(dayjs().startOf('month'));
  const [daysRangeEnd, setDaysRangeEnd] = useState(dayjs().endOf('month'));

  const data = DUMMY_DATA[org]?.projects[project] || {};
  const stats = data.stats || [];
  let filteredStats = [];
  if (pickerType === 'date') {
    filteredStats = stats.filter(s => dayjs(s.date).isSame(selectedDate, 'month'));
  } else if (pickerType === 'month') {
    filteredStats = stats.filter(s => dayjs(s.date).isSame(selectedMonth, 'month'));
  } else if (pickerType === 'range') {
    filteredStats = stats.filter(s => dayjs(s.date).isAfter(rangeStart.subtract(1, 'day')) && dayjs(s.date).isBefore(rangeEnd.add(1, 'day')));
  }
  // DAU: always from days array (single date = value, month/range = avg)
  let dau = 0;
  if (pickerType === 'date') {
    const day = data.days?.find(d => dayjs(d.date).isSame(selectedDate, 'day'));
    dau = day ? day.total : 0;
  } else if (pickerType === 'month') {
    const daysInMonth = data.days?.filter(d => dayjs(d.date).isSame(selectedMonth, 'month')) || [];
    dau = daysInMonth.length
      ? Math.round(daysInMonth.reduce((sum, d) => sum + (d.total || 0), 0) / daysInMonth.length)
      : 0;
  } else if (pickerType === 'range') {
    const daysInRange = data.days?.filter(d => dayjs(d.date).isAfter(rangeStart.subtract(1, 'day')) && dayjs(d.date).isBefore(rangeEnd.add(1, 'day'))) || [];
    dau = daysInRange.length
      ? Math.round(daysInRange.reduce((sum, d) => sum + (d.total || 0), 0) / daysInRange.length)
      : 0;
  }
  // MAU/Installs as before from stats
  const displayStats =
    pickerType === 'date'
      ? (filteredStats[0] || { mau: 0, installs: 0 })
      : filteredStats.length === 1
        ? filteredStats[0]
        : {
            mau: filteredStats.reduce((a, b) => a + (b.mau || 0), 0),
            installs: filteredStats.reduce((a, b) => a + (b.installs || 0), 0),
          };

  // Filter days data for the chart
  let filteredDays = [];
  if (daysPickerType === 'date') {
    filteredDays = data.days?.filter(d => dayjs(d.date).isSame(daysSelectedDate, 'day')) || [];
  } else if (daysPickerType === 'month') {
    filteredDays = data.days?.filter(d => dayjs(d.date).isSame(daysSelectedMonth, 'month')) || [];
  } else if (daysPickerType === 'range') {
    filteredDays = data.days?.filter(d => dayjs(d.date).isAfter(daysRangeStart.subtract(1, 'day')) && dayjs(d.date).isBefore(daysRangeEnd.add(1, 'day'))) || [];
  }

  // Picker UI (moved inside component)
  const pickerUI = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <FormControl size="small">
        <InputLabel id="picker-type-label">Type</InputLabel>
        <Select
          labelId="picker-type-label"
          value={pickerType}
          label="Type"
          onChange={e => setPickerType(e.target.value)}
          sx={{ minWidth: 100 }}
        >
          <MenuItem value="date">Date</MenuItem>
          <MenuItem value="month">Month</MenuItem>
          <MenuItem value="range">Range</MenuItem>
        </Select>
      </FormControl>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {pickerType === 'date' && (
          <DatePicker
            label="Select Date"
            value={selectedDate}
            onChange={setSelectedDate}
            slotProps={{ textField: { size: 'small' } }}
          />
        )}
        {pickerType === 'month' && (
          <DatePicker
            views={['year', 'month']}
            label="Select Month"
            value={selectedMonth}
            onChange={setSelectedMonth}
            slotProps={{ textField: { size: 'small' } }}
          />
        )}
        {pickerType === 'range' && (
          <>
            <DatePicker
              label="Start"
              value={rangeStart}
              onChange={setRangeStart}
              slotProps={{ textField: { size: 'small' } }}
              maxDate={rangeEnd}
            />
            <DatePicker
              label="End"
              value={rangeEnd}
              onChange={setRangeEnd}
              slotProps={{ textField: { size: 'small' } }}
              minDate={rangeStart}
            />
          </>
        )}
      </LocalizationProvider>
    </Box>
  );

  // Picker UI for Users - Day Wise chart
  const daysPickerUI = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, justifyContent: 'flex-end' }}>
      <FormControl size="small">
        <InputLabel id="days-picker-type-label">Type</InputLabel>
        <Select
          labelId="days-picker-type-label"
          value={daysPickerType}
          label="Type"
          onChange={e => setDaysPickerType(e.target.value)}
          sx={{ minWidth: 100 }}
        >
          <MenuItem value="date">Date</MenuItem>
          <MenuItem value="month">Month</MenuItem>
          <MenuItem value="range">Range</MenuItem>
        </Select>
      </FormControl>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {daysPickerType === 'date' && (
          <DatePicker
            label="Select Date"
            value={daysSelectedDate}
            onChange={setDaysSelectedDate}
            slotProps={{ textField: { size: 'small' } }}
          />
        )}
        {daysPickerType === 'month' && (
          <DatePicker
            views={['year', 'month']}
            label="Select Month"
            value={daysSelectedMonth}
            onChange={setDaysSelectedMonth}
            slotProps={{ textField: { size: 'small' } }}
          />
        )}
        {daysPickerType === 'range' && (
          <>
            <DatePicker
              label="Start"
              value={daysRangeStart}
              onChange={setDaysRangeStart}
              slotProps={{ textField: { size: 'small' } }}
              maxDate={daysRangeEnd}
            />
            <DatePicker
              label="End"
              value={daysRangeEnd}
              onChange={setDaysRangeEnd}
              slotProps={{ textField: { size: 'small' } }}
              minDate={daysRangeStart}
            />
          </>
        )}
      </LocalizationProvider>
    </Box>
  );

  return (
    <div className="dashboard">
      <div className="welcome-box" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '32px 36px', background: '#eaffd6' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, color: '#1db954', fontWeight: 700, fontSize: '1.5rem' }}>
            Welcome back <span role="img" aria-label="wave">👋</span>
          </h2>
          <div style={{ fontWeight: 600, color: '#009e60', fontSize: '1.18rem', margin: '8px 0 10px 0' }}>{email}</div>
          <p style={{ margin: 0, color: '#3a3a3a', fontSize: '1.05rem', fontWeight: 400 }}>
            Get a complete overview of your performance — from Daily, Weekly, and Monthly Active Users (DAU, WAU, MAU), to installs, new vs. returning users, gameplay trends, and detailed insights based on city, country, and continent coming soon.
          </p>
        </div>
        <div style={{ flexShrink: 0, marginLeft: 32 }}>
          <img src={process.env.PUBLIC_URL + '/temu-banner.png'} alt="Temu Banner" style={{ width: 260, maxWidth: '30vw', height: 'auto', borderRadius: 18 }} />
        </div>
      </div>
      <div className="tabs">
        {['Overview', 'Engagement', 'Ad Metrics'].map(t => (
          <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>
      {tab === 'Overview' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#222', ml: 1 }}>
              Stats
            </Box>
            {pickerUI}
          </Box>
          <div className="stats-grid mb-8">
            <StatCard label="DAU" value={dau} color="#d3f5e7" />
            <StatCard label="MAU" value={displayStats.mau || 0} color="#d3eaf5" />
            <StatCard label="INSTALLS" value={displayStats.installs || 0} color="#ecd3f5" />
          </div>
        </>
      )}
      {tab === 'Overview' && (
        <div className="dashboard-charts-grid w-full gap-8 mb-10">
          {/* <ChartCard title="Active Users - City Wise">
            <ChartContainer>
              {width => (
                <BarChart
                  layout="horizontal"
                  yAxis={[{ data: data.cities.map(c => c.name), scaleType: 'band', width: 80 }]}
                  series={[{ data: data.cities.map(c => c.active), color: '#1db954', label: 'Active Users' }]}
                  width={width}
                  height={320}
                  margin={{ left: 20, right: 20, top: 20, bottom: 30 }}
                />
              )}
            </ChartContainer>
          </ChartCard>
          <ChartCard title="Users - Country Wise">
            <ChartContainer>
              {width => (
                <BarChart
                  layout="horizontal"
                  yAxis={[{ data: data.countries.map(c => c.name), scaleType: 'band', width: 80 }]}
                  series={[
                    { data: data.countries.map(c => c.active), label: 'Active Users', color: '#1db954' },
                    { data: data.countries.map(c => c.new), label: 'New Users', color: '#00bcd4' },
                  ]}
                  width={width}
                  height={320}
                  margin={{ left: 20, right: 20, top: 20, bottom: 30 }}
                  legend={{ position: { vertical: 'top', horizontal: 'right' } }}
                />
              )}
            </ChartContainer>
          </ChartCard> */}
          <div className="dashboard-charts-full md:col-span-2">
            <ChartCard
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <span>Users - Day Wise</span>
                  {daysPickerUI}
                </Box>
              }
            >
              <ChartContainer height={340} minHeight={260}>
                {width => (
                  <LineChart
                    xAxis={[{ data: filteredDays.map(d => d.date), scaleType: 'point', label: 'Date' }]}
                    yAxis={[{ label: 'Users', width: 80 }]}
                    series={[
                      { data: filteredDays.map(d => d.total), label: 'Total Users', color: '#1db954' },
                      // { data: filteredDays.map(d => d.active), label: 'Active Users', color: '#00bcd4' },
                      // { data: filteredDays.map(d => d.new), label: 'New Users', color: '#ffc107' },
                    ]}
                    width={width}
                    height={340}
                    margin={{ left: 20, right: 20, top: 20, bottom: 40 }}
                    legend={{ position: { vertical: 'top', horizontal: 'right' } }}
                  />
                )}
              </ChartContainer>
            </ChartCard>
          </div>
          {/* MAU and Installs Trends Row */}
          <div className="dashboard-charts-full md:col-span-2" style={{ width: '100%', marginTop: 32 }}>
            <div style={{ display: 'flex', width: '100%', gap: 24 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ChartCard title="MAU Trend">
                  <ChartContainer height={260} minHeight={180}>
                    {width => (
                      <LineChart
                        xAxis={[{ data: (data.stats || []).map(s => s.date), scaleType: 'point', label: 'Month' }]}
                        yAxis={[{ label: 'MAU', width: 80 }]}
                        series={[
                          { data: (data.stats || []).map(s => s.mau), label: 'MAU', color: '#1db954' },
                        ]}
                        width={width}
                        height={260}
                        margin={{ left: 20, right: 20, top: 20, bottom: 40 }}
                        legend={{ position: { vertical: 'top', horizontal: 'right' } }}
                      />
                    )}
                  </ChartContainer>
                </ChartCard>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ChartCard title="Installs Trend">
                  <ChartContainer height={260} minHeight={180}>
                    {width => (
                      <LineChart
                        xAxis={[{ data: (data.stats || []).map(s => s.date), scaleType: 'point', label: 'Month' }]}
                        yAxis={[{ label: 'Installs', width: 80 }]}
                        series={[
                          { data: (data.stats || []).map(s => s.installs), label: 'Installs', color: '#009e60' },
                        ]}
                        width={width}
                        height={260}
                        margin={{ left: 20, right: 20, top: 20, bottom: 40 }}
                        legend={{ position: { vertical: 'top', horizontal: 'right' } }}
                      />
                    )}
                  </ChartContainer>
                </ChartCard>
              </div>
            </div>
          </div>
        </div>
      )}
      {tab !== 'Overview' && (
        <div style={{ padding: 32, color: '#888' }}>[{tab} coming soon]</div>
      )}
    </div>
  );
};

export default Dashboard; 